/**
 * GCSA chrome defaults — Logo + mobile sidebar nav snapshots.
 * 与官网 `common.nav` + `useMobileNavSections` 顺序一致。
 */

import {
  GCSA_MAIN_ORIGIN,
  GCSA_MEDIA_ORIGIN,
  type SiteFooterLocale
} from './gcsaFooterContent';

export type GcsaChromeLocale = SiteFooterLocale;

export type GcsaChromeNavIconId =
  | 'home'
  | 'products'
  | 'forums'
  | 'media'
  | 'aiResearchInstitute'
  | 'strategicPartners'
  | 'contact'
  | 'about';

/** 侧栏可用图标 = 官网 chrome + 站点扩展（wallet / api） */
export type GcsaNavIconId = GcsaChromeNavIconId | 'wallet' | 'api';

export type GcsaMobileNavChild = {
  key: string;
  label: string;
  href: string;
  external?: boolean;
};

export type GcsaMobileNavItem = {
  key: string;
  label: string;
  href: string;
  /** 预设图标 id（官网 / 扩展库） */
  icon?: GcsaNavIconId;
  /**
   * 自定义 SVG 内部 markup（`<path>` / `<g>` 等），优先于 icon。
   * 各站点可自带语义图标，无需改 gcsa-ui 源码。
   */
  iconSvg?: string;
  external?: boolean;
  children?: GcsaMobileNavChild[];
};

export type GcsaLogoSnapshot = {
  href: string;
  title: string;
  tagline: string;
  srLabel: string;
};

export type GcsaMobileSidebarSnapshot = {
  menuAriaLabel: string;
  closeAriaLabel: string;
  navAriaLabel: string;
  logo: GcsaLogoSnapshot;
  navItems: GcsaMobileNavItem[];
};

const DEFAULT_HREFS: Record<GcsaChromeNavIconId, string> = {
  home: `${GCSA_MAIN_ORIGIN}/`,
  products: `${GCSA_MAIN_ORIGIN}/products`,
  media: GCSA_MEDIA_ORIGIN,
  forums: `${GCSA_MAIN_ORIGIN}/forums`,
  aiResearchInstitute: `${GCSA_MAIN_ORIGIN}/ai-research-institute`,
  strategicPartners: `${GCSA_MAIN_ORIGIN}/strategic-partners`,
  contact: `${GCSA_MAIN_ORIGIN}/contact`,
  about: `${GCSA_MAIN_ORIGIN}/about`
};

const EN_US_HREFS: Record<GcsaChromeNavIconId, string> = {
  home: `${GCSA_MAIN_ORIGIN}/en-US/`,
  products: `${GCSA_MAIN_ORIGIN}/en-US/products`,
  media: GCSA_MEDIA_ORIGIN,
  forums: `${GCSA_MAIN_ORIGIN}/en-US/forums`,
  aiResearchInstitute: `${GCSA_MAIN_ORIGIN}/en-US/ai-research-institute`,
  strategicPartners: `${GCSA_MAIN_ORIGIN}/en-US/strategic-partners`,
  contact: `${GCSA_MAIN_ORIGIN}/en-US/contact`,
  about: `${GCSA_MAIN_ORIGIN}/en-US/about`
};

const LOGO_BASE: GcsaLogoSnapshot = {
  href: GCSA_MAIN_ORIGIN,
  title: 'GCSA',
  tagline: 'www.gcsa.org',
  srLabel: 'GCSA - Global Cybersecurity Alliance'
};

/** 首页 → 生态 → 媒体 → 动态 → AI 研究所 → 战略合作 → 加入 → 关于 */
function buildNav(
  labels: Record<GcsaChromeNavIconId, string>,
  hrefs: Record<GcsaChromeNavIconId, string> = DEFAULT_HREFS
): GcsaMobileNavItem[] {
  return [
    { key: 'home', label: labels.home, href: hrefs.home, icon: 'home', external: true },
    {
      key: 'products',
      label: labels.products,
      href: hrefs.products,
      icon: 'products',
      external: true
    },
    {
      key: 'media',
      label: labels.media,
      href: hrefs.media,
      icon: 'media',
      external: true
    },
    {
      key: 'forums',
      label: labels.forums,
      href: hrefs.forums,
      icon: 'forums',
      external: true
    },
    {
      key: 'aiResearchInstitute',
      label: labels.aiResearchInstitute,
      href: hrefs.aiResearchInstitute,
      icon: 'aiResearchInstitute',
      external: true
    },
    {
      key: 'strategicPartners',
      label: labels.strategicPartners,
      href: hrefs.strategicPartners,
      icon: 'strategicPartners',
      external: true
    },
    {
      key: 'contact',
      label: labels.contact,
      href: hrefs.contact,
      icon: 'contact',
      external: true
    },
    {
      key: 'about',
      label: labels.about,
      href: hrefs.about,
      icon: 'about',
      external: true
    }
  ];
}

export const GCSA_CHROME_ZH_CN: GcsaMobileSidebarSnapshot = {
  menuAriaLabel: '导航菜单',
  closeAriaLabel: '关闭菜单',
  navAriaLabel: '页面导航',
  logo: { ...LOGO_BASE },
  navItems: buildNav({
    home: '首页',
    products: 'GCSA 生态产品',
    media: 'GCSA 媒体',
    forums: 'GCSA 动态',
    aiResearchInstitute: 'GCSA AI网络安全研究中心',
    strategicPartners: 'GCSA 战略合作及顾问',
    contact: '加入联盟',
    about: '关于联盟'
  })
};

export const GCSA_CHROME_ZH_TW: GcsaMobileSidebarSnapshot = {
  menuAriaLabel: '導航菜單',
  closeAriaLabel: '關閉菜單',
  navAriaLabel: '頁面導航',
  logo: { ...LOGO_BASE },
  navItems: buildNav({
    home: '首頁',
    products: 'GCSA 生態產品',
    media: 'GCSA 媒體',
    forums: 'GCSA 動態',
    aiResearchInstitute: 'GCSA AI網絡安全研究中心',
    strategicPartners: 'GCSA 戰略合作及顧問',
    contact: '加入聯盟',
    about: '關於聯盟'
  })
};

export const GCSA_CHROME_EN_US: GcsaMobileSidebarSnapshot = {
  menuAriaLabel: 'Navigation menu',
  closeAriaLabel: 'Close menu',
  navAriaLabel: 'Navigation',
  logo: { ...LOGO_BASE },
  navItems: buildNav(
    {
      home: 'Home',
      products: 'GCSA Ecosystem Products',
      media: 'GCSA Media',
      forums: 'GCSA Updates',
      aiResearchInstitute: 'GCSA AI Cybersecurity Research Institute',
      strategicPartners: 'GCSA Strategic Partners & Advisors',
      contact: 'Join the Alliance',
      about: 'About Alliance'
    },
    EN_US_HREFS
  )
};

const BY_LOCALE: Record<GcsaChromeLocale, GcsaMobileSidebarSnapshot> = {
  'zh-CN': GCSA_CHROME_ZH_CN,
  'zh-TW': GCSA_CHROME_ZH_TW,
  'en-US': GCSA_CHROME_EN_US
};

export function resolveGcsaChrome(
  locale: GcsaChromeLocale = 'zh-CN'
): GcsaMobileSidebarSnapshot {
  return BY_LOCALE[locale] ?? GCSA_CHROME_ZH_CN;
}

export function resolveGcsaLogo(
  locale: GcsaChromeLocale = 'zh-CN'
): GcsaLogoSnapshot {
  return resolveGcsaChrome(locale).logo;
}
